/**
 * Document operations — the only way a document is ever changed.
 *
 * ⚠️ THESE LIVED IN THE EDITOR AND HAVE BEEN MOVED INTO THE MODEL, which is the most consequential
 * change in the migration. In the predecessor they were `web/builder/src/editor/store.ts`, and its
 * own CLAUDE.md recorded the cost: `removePage` refusing to delete the last page was *"enforced in
 * the op precisely so no caller can route around it"* — and then the file could not be unit-tested
 * at all, because Node's ESM resolver could not follow its extensionless value imports. The
 * invariant that existed so no caller could route around it was covered only by a Playwright test
 * driving a button.
 *
 * Two things follow from moving them here, and the second is the real reason:
 *
 *   1. They are testable, with no browser and no bundler.
 *   2. **The AI patch loop and the human editor are the same caller.** A model that adds a block
 *      calls `addBlock`; so does the palette. There is no second path that can produce a document
 *      the editor cannot represent — which is the failure mode of every "let the model emit JSON"
 *      design, and it is unfixable once it ships because the bad documents are already stored.
 *
 * Every op returns a NEW `Site`. Immutability is not a style preference here: an undo stack is
 * `Site[]`, and a patch that mutates in place makes every entry in it the same object.
 */

import { defaultProps, defaultVariant, type BlockDef } from './prop-types.ts'
import { getBlockDef } from './registry/index.ts'
import { newId } from './id.ts'
import { isSlug } from './validate.ts'
import type { Block, BlockWidth, Page, Site, Text } from './index.ts'

/**
 * How any block in the document is addressed.
 *
 * Three cases and no more, because the page is flat. This type is the payoff of the no-nesting
 * decision in `types.ts`: there is no `{ scope: 'page', pageId, blockId, slot, childIndex }` form,
 * so there is nothing for a patch to get subtly wrong.
 */
export type BlockRef =
  | { scope: 'navbar' }
  | { scope: 'footer' }
  | { scope: 'page'; pageId: string; blockId: string }

export function refsEqual(a: BlockRef | null, b: BlockRef | null): boolean {
  if (!a || !b) return a === b
  if (a.scope !== b.scope) return false
  if (a.scope === 'page' && b.scope === 'page') return a.pageId === b.pageId && a.blockId === b.blockId
  return true
}

export function getBlock(site: Site, ref: BlockRef): Block | undefined {
  if (ref.scope === 'navbar') return site.navbar
  if (ref.scope === 'footer') return site.footer
  return site.pages.find((p) => p.id === ref.pageId)?.blocks.find((b) => b.id === ref.blockId)
}

function mapBlock(site: Site, ref: BlockRef, fn: (b: Block) => Block): Site {
  if (ref.scope === 'navbar') return { ...site, navbar: fn(site.navbar) }
  if (ref.scope === 'footer') return { ...site, footer: fn(site.footer) }
  return {
    ...site,
    pages: site.pages.map((p) =>
      p.id !== ref.pageId ? p : { ...p, blocks: p.blocks.map((b) => (b.id !== ref.blockId ? b : fn(b))) },
    ),
  }
}

function mapPage(site: Site, pageId: string, fn: (p: Page) => Page): Site {
  return { ...site, pages: site.pages.map((p) => (p.id !== pageId ? p : fn(p))) }
}

// --- block ops -----------------------------------------------------------------------------------

export function setProp(site: Site, ref: BlockRef, key: string, value: unknown): Site {
  return mapBlock(site, ref, (b) => ({ ...b, props: { ...b.props, [key]: value } }))
}

export function setVariant(site: Site, ref: BlockRef, variant: string): Site {
  return mapBlock(site, ref, (b) => ({ ...b, variant }))
}

/** Set a block's share of the row. `full` is stored as ABSENT rather than as the string, so a block
 *  that never opted into a width carries no layout key at all — which keeps a diff between two
 *  documents about what actually changed. */
export function setWidth(site: Site, ref: BlockRef, width: BlockWidth): Site {
  return mapBlock(site, ref, (b) => {
    const next = { ...b }
    if (width === 'full') delete next.width
    else next.width = width
    return next
  })
}

export function makeBlock(def: BlockDef): Block {
  const block: Block = { id: newId('blk'), type: def.type, v: def.v, props: defaultProps(def) }
  const variant = defaultVariant(def)
  if (variant !== undefined) block.variant = variant
  return block
}

/**
 * Add a page block from the palette (or from a model), optionally at an index.
 *
 * Returns `null` for a type that is not a page block rather than throwing, and rather than silently
 * doing nothing. Both callers need to *report* the refusal: the editor as a disabled palette entry,
 * the AI loop as a tool error the model can correct on the next turn. A silent no-op reads to a
 * model as success, and it will keep going.
 */
export function addBlock(site: Site, pageId: string, type: string, at?: number): { site: Site; ref: BlockRef } | null {
  const def = getBlockDef(type)
  if (!def || def.scope !== 'page') return null
  if (!site.pages.some((p) => p.id === pageId)) return null

  const block = makeBlock(def)
  return {
    site: mapPage(site, pageId, (p) => {
      const blocks = [...p.blocks]
      blocks.splice(at ?? blocks.length, 0, block)
      return { ...p, blocks }
    }),
    ref: { scope: 'page', pageId, blockId: block.id },
  }
}

export function removeBlock(site: Site, pageId: string, blockId: string): Site {
  return mapPage(site, pageId, (p) => ({ ...p, blocks: p.blocks.filter((b) => b.id !== blockId) }))
}

/** Duplicate a block directly after itself, with fresh ids. The most-used editor action after
 *  "add", and the one a model should reach for when a customer says "another one like that". */
export function duplicateBlock(site: Site, pageId: string, blockId: string): { site: Site; ref: BlockRef } | null {
  const page = site.pages.find((p) => p.id === pageId)
  const index = page?.blocks.findIndex((b) => b.id === blockId) ?? -1
  const source = index >= 0 ? page!.blocks[index] : undefined
  if (!source) return null
  const copy: Block = JSON.parse(JSON.stringify(source))
  copy.id = newId('blk')
  return {
    site: mapPage(site, pageId, (p) => {
      const blocks = [...p.blocks]
      blocks.splice(index + 1, 0, copy)
      return { ...p, blocks }
    }),
    ref: { scope: 'page', pageId, blockId: copy.id },
  }
}

/** Move a block one place up or down. A plain splice — which is what a flat page buys. */
export function moveBlock(site: Site, pageId: string, blockId: string, dir: -1 | 1): Site {
  return mapPage(site, pageId, (p) => {
    const i = p.blocks.findIndex((b) => b.id === blockId)
    const j = i + dir
    if (i < 0 || j < 0 || j >= p.blocks.length) return p
    const blocks = [...p.blocks]
    const a = blocks[i]!
    const b = blocks[j]!
    blocks[i] = b
    blocks[j] = a
    return { ...p, blocks }
  })
}

// --- page ops -------------------------------------------------------------------------------------

/** A slug no other page uses. New pages get `page`, then `page-2`, `page-3` — always valid, so a
 *  fresh page is never born failing the publish-time uniqueness check. */
export function freeSlug(site: Site, base = 'page'): string {
  const used = new Set(site.pages.map((p) => p.slug))
  if (!used.has(base)) return base
  for (let n = 2; ; n++) {
    const s = `${base}-${n}`
    if (!used.has(s)) return s
  }
}

export function addPage(site: Site, title?: Text, slugBase?: string): { site: Site; pageId: string } {
  const page: Page = {
    id: newId('page'),
    title: title ?? { en: 'New page', ar: 'صفحة جديدة' },
    slug: freeSlug(site, slugBase && isSlug(slugBase) ? slugBase : 'page'),
    blocks: [],
  }
  return { site: { ...site, pages: [...site.pages, page] }, pageId: page.id }
}

/**
 * Remove a page.
 *
 * ⚠️ REFUSES THE LAST ONE, IN THE OP. "A site needs at least one page" is enforced here rather than
 * by disabling a button, precisely so that no caller — not the editor, not a model, not a future
 * bulk action — can route around it and leave a document the renderer has nothing to draw. The
 * editor *also* disables the button, and shows why; that is a courtesy on top of the guarantee, not
 * the guarantee.
 *
 * Links that pointed at the removed page are not rewritten. They are caught by `validateSite`'s
 * unresolved-link pass and reported with the page they are on, because silently retargeting somebody
 * else's navigation is worse than telling them it broke.
 */
export function removePage(site: Site, pageId: string): Site {
  if (site.pages.length <= 1) return site
  return { ...site, pages: site.pages.filter((p) => p.id !== pageId) }
}

export function movePage(site: Site, pageId: string, dir: -1 | 1): Site {
  const i = site.pages.findIndex((p) => p.id === pageId)
  const j = i + dir
  if (i < 0 || j < 0 || j >= site.pages.length) return site
  const pages = [...site.pages]
  const a = pages[i]!
  const b = pages[j]!
  pages[i] = b
  pages[j] = a
  return { ...site, pages }
}

export function setPageTitle(site: Site, pageId: string, title: Text): Site {
  return mapPage(site, pageId, (p) => ({ ...p, title }))
}

/** Set a page's address. Stored RAW as typed — the manual override the design wants — with
 *  `slugState` warning inline and `validateSite` the authority at publish. Silently rewriting what
 *  somebody typed is how a customer ends up with a URL they did not choose and cannot find. */
export function setPageSlug(site: Site, pageId: string, slug: string): Site {
  return mapPage(site, pageId, (p) => ({ ...p, slug }))
}

export function slugState(site: Site, pageId: string, slug: string): 'ok' | 'malformed' | 'duplicate' {
  if (!isSlug(slug)) return 'malformed'
  if (site.pages.some((p) => p.id !== pageId && p.slug === slug)) return 'duplicate'
  return 'ok'
}

// --- site ops -------------------------------------------------------------------------------------

/**
 * Switch template.
 *
 * ⚠️ A ONE-LINE CHANGE, AND IT MUST STAY ONE. A template is tokens only (`@ksa/render`'s TEMPLATES) —
 * no markup, no per-template block components. That is what makes "try another look" free, and it is
 * also what makes a thirteenth block work under every template on the day it ships rather than
 * needing thirteen × N implementations.
 *
 * `tmpl`-scoped props are NOT reset here, because every template declares the same tmpl fields
 * today. The moment one template offers a ground the next does not, this function has to reset
 * unknown tmpl props to the new template's defaults — and the `Scope` field exists so that change is
 * mechanical rather than archaeological.
 */
export function setTemplate(site: Site, template: string): Site {
  return { ...site, template }
}

export function setSiteTitle(site: Site, title: Text): Site {
  return { ...site, title }
}

export function setDefaultLang(site: Site, defaultLang: 'en' | 'ar'): Site {
  return { ...site, defaultLang }
}
